// src/pages/platform-admin/Assigned.js

import React, { useState, useEffect, useRef } from 'react';
import {
    Container,
    Typography,
    Button,
    Paper,
    Box,
    Grid,
    Card,
    CardContent,
    Avatar,
    Chip,
    useTheme,
    Tooltip,
    CircularProgress,
    Fade,
    Zoom,
    Divider,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    InputAdornment,
    Badge,
    Stack,
    useMediaQuery,
    Tab,
    Tabs,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    TableSortLabel,
    Menu,
    MenuItem,
    ListItemIcon,
    ListItemText,
    FormControl,
    InputLabel,
    Select,
    OutlinedInput,
    Checkbox,
    ListItemButton,
    Collapse,
    Alert,
    Snackbar,
    CardActions
} from '@mui/material';
import {
    Refresh as RefreshIcon,
    Person,
    Email,
    Phone,
    CheckCircle,
    Warning,
    Schedule,
    School,
    Verified,
    Pending,
    CalendarToday,
    People,
    Flag,
    Search,
    FilterList,
    Download,
    Print,
    Visibility,
    Close,
    Assignment,
    SupervisorAccount,
    Timeline,
    TrendingUp,
    Assessment,
    DateRange,
    Clear,
    ExpandMore,
    ExpandLess,
    Dashboard,
    List as ListIcon,
    GridView,
    Sort,
    FilterAlt,
    Cancel,
    CheckCircleOutline,
    HourglassEmpty
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import useCustomToast from '../../hooks/useToast';
import {
    APIAssignStudentList,
    APIStudentProfileImage,
    APISupervisorProfileImage,
    APIAssignStudentStats
} from '../../apiServices/APIs';
import { 
    formatCreatedAtDate, 
    getUserDetails,
    getImageUrl
} from '../../utils/helpers';
import { apiService } from '../../apiServices/ApiService';
import LoadingState from '../../components/LoadingState';
import ErrorState from '../../components/ErrorState';
import PlatformAdminBar from '../../components/PlatformAdminBar';
import { FullWidthContainer } from '../../components/FullWidthContainer';

// Helper function to get initials from user name
const getInitials = (name) => {
    if (!name) return 'NA';
    return name
        .split(' ')
        .map(word => word.charAt(0))
        .join('')
        .toUpperCase()
        .substring(0, 2);
};

// Helper function to get avatar color based on user name
const stringToColor = (string) => {
    if (!string) return '#1976d2';
    let hash = 0;
    for (let i = 0; i < string.length; i++) {
        hash = string.charCodeAt(i) + ((hash << 5) - hash);
    }
    let color = '#';
    for (let i = 0; i < 3; i++) {
        const value = (hash >> (i * 8)) & 0xFF;
        color += ('00' + value.toString(16)).substr(-2);
    }
    return color;
};

// Status configurations
const statusConfig = {
    active: { color: 'success', icon: <Verified />, label: 'Active' },
    pending: { color: 'warning', icon: <Pending />, label: 'Pending' },
    completed: { color: 'info', icon: <CheckCircle />, label: 'Completed' },
    cancelled: { color: 'error', icon: <Cancel />, label: 'Cancelled' }
};

const Assigned = () => {
    const theme = useTheme();
    const navigate = useNavigate();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isTablet = useMediaQuery(theme.breakpoints.down('md'));
    
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [assignments, setAssignments] = useState([]);
    const [filteredAssignments, setFilteredAssignments] = useState([]);
    const [stats, setStats] = useState(null);
    const [activeTab, setActiveTab] = useState(0);
    const [viewMode, setViewMode] = useState('grid'); // 'grid', 'list'
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [dateRange, setDateRange] = useState({ start: '', end: '' });
    const [sortBy, setSortBy] = useState('assigned_date');
    const [sortOrder, setSortOrder] = useState('desc');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(12);
    const [selectedAssignment, setSelectedAssignment] = useState(null);
    const [openViewDialog, setOpenViewDialog] = useState(false);
    const [anchorEl, setAnchorEl] = useState(null);
    const [filterAnchorEl, setFilterAnchorEl] = useState(null);
    const [expandedFilters, setExpandedFilters] = useState(false);
    
    const showToast = useCustomToast();
    const isMounted = useRef(true);
    const adminId = getUserDetails("Id");

    useEffect(() => {
        isMounted.current = true;
        apiService.setToastFunction(showToast);
        fetchAllData();
        
        // Auto-refresh every 3 minutes
        const interval = setInterval(() => {
            fetchAllData(true);
        }, 180000);
        
        return () => {
            isMounted.current = false;
            clearInterval(interval);
        };
    }, []);

    useEffect(() => {
        applyFiltersAndSort();
    }, [assignments, searchTerm, statusFilter, dateRange, sortBy, sortOrder, activeTab]);

    const fetchAllData = async (silent = false) => {
        if (!silent) setLoading(true);
        
        try {
            await Promise.all([
                fetchAssignments(),
                fetchStats()
            ]);
        } catch (error) {
            if (!silent) {
                setError("Failed to load assignment data");
                console.error("Fetch error:", error);
            }
        } finally {
            if (!silent && isMounted.current) {
                setLoading(false);
            }
        }
    };

    const fetchAssignments = async () => {
        try {
            const result = await apiService.list(APIAssignStudentList);
            if (result.success && isMounted.current) {
                const data = Array.isArray(result.data) ? result.data : [];
                setAssignments(data);
            } else {
                console.error("Failed to fetch assignments:", result?.message);
                setAssignments([]);
            }
        } catch (error) {
            console.error("Error fetching assignments:", error);
            setAssignments([]);
        }
    };

    const fetchStats = async () => {
        try {
            const result = await apiService.list(APIAssignStudentStats);
            if (result.success && result.data && isMounted.current) {
                setStats(result.data);
            }
        } catch (error) {
            console.error("Error fetching stats:", error);
        }
    };

    const applyFiltersAndSort = () => {
        let filtered = [...assignments];
        
        // Filter by tab status
        if (activeTab === 1) filtered = filtered.filter(a => a.status === 'active');
        else if (activeTab === 2) filtered = filtered.filter(a => a.status === 'pending');
        else if (activeTab === 3) filtered = filtered.filter(a => a.status === 'completed');
        
        // Filter by search term
        if (searchTerm) {
            const term = searchTerm.toLowerCase();
            filtered = filtered.filter(a => 
                a.student?.name?.toLowerCase().includes(term) ||
                a.student?.email?.toLowerCase().includes(term) ||
                a.student?.registration_id?.toLowerCase().includes(term) ||
                a.supervisor?.name?.toLowerCase().includes(term) ||
                a.supervisor?.email?.toLowerCase().includes(term) ||
                a.assignment_id?.toLowerCase().includes(term)
            );
        }
        
        // Filter by status dropdown
        if (statusFilter !== 'all') {
            filtered = filtered.filter(a => a.status === statusFilter);
        }
        
        // Filter by date range
        if (dateRange.start) {
            filtered = filtered.filter(a => new Date(a.assigned_date) >= new Date(dateRange.start));
        }
        if (dateRange.end) {
            filtered = filtered.filter(a => new Date(a.assigned_date) <= new Date(dateRange.end));
        }
        
        // Sort
        filtered.sort((a, b) => {
            let aVal, bVal;
            switch(sortBy) {
                case 'student_name':
                    aVal = a.student?.name || '';
                    bVal = b.student?.name || '';
                    break;
                case 'supervisor_name':
                    aVal = a.supervisor?.name || '';
                    bVal = b.supervisor?.name || '';
                    break;
                case 'assigned_date':
                    aVal = new Date(a.assigned_date);
                    bVal = new Date(b.assigned_date);
                    break;
                case 'status':
                    aVal = a.status || '';
                    bVal = b.status || '';
                    break;
                default:
                    aVal = a[sortBy] || '';
                    bVal = b[sortBy] || '';
            }
            
            if (sortOrder === 'asc') {
                return aVal > bVal ? 1 : -1;
            } else {
                return aVal < bVal ? 1 : -1;
            }
        });
        
        setFilteredAssignments(filtered);
        setPage(0);
    };

    const handleOpenViewDialog = (assignment) => {
        setSelectedAssignment(assignment);
        setOpenViewDialog(true);
    };

    const handleCloseViewDialog = () => {
        setOpenViewDialog(false);
        setSelectedAssignment(null);
    };

    const handleExportData = () => {
        const data = filteredAssignments.map(a => ({
            'Assignment ID': a.assignment_id,
            'Student Name': a.student?.name,
            'Student Email': a.student?.email,
            'Student Registration': a.student?.registration_id,
            'Supervisor Name': a.supervisor?.name,
            'Supervisor Email': a.supervisor?.email,
            'Status': a.status,
            'Assigned Date': formatCreatedAtDate(a.assigned_date),
            'Completion Date': a.completion_date ? formatCreatedAtDate(a.completion_date) : 'N/A',
            'Notes': a.assignment_notes || 'N/A'
        }));
        
        const csv = convertToCSV(data);
        const blob = new Blob([csv], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `assignments_export_${new Date().toISOString().split('T')[0]}.csv`;
        a.click();
        window.URL.revokeObjectURL(url);
        showToast("Success", "Data exported successfully", "success");
    };

    const convertToCSV = (data) => {
        const headers = Object.keys(data[0]);
        const csvRows = [];
        csvRows.push(headers.join(','));
        
        for (const row of data) {
            const values = headers.map(header => {
                const val = row[header] || '';
                return `"${val.toString().replace(/"/g, '""')}"`;
            });
            csvRows.push(values.join(','));
        }
        
        return csvRows.join('\n');
    };

    const handlePrint = () => {
        window.print();
    };

    const getProfileImageUrl = (user, type = 'student') => {
        if (type === 'supervisor' && user?.profile_image_path) {
            return getImageUrl(APISupervisorProfileImage, user.profile_image_path);
        }
        if (user?.profile_image) {
            return getImageUrl(APIStudentProfileImage, user.profile_image);
        }
        return null;
    };

    const handleSort = (field) => {
        if (sortBy === field) {
            setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
        } else {
            setSortBy(field);
            setSortOrder('asc');
        }
    };

    const clearFilters = () => {
        setSearchTerm('');
        setStatusFilter('all');
        setDateRange({ start: '', end: '' });
        setSortBy('assigned_date');
        setSortOrder('desc');
        setActiveTab(0);
    };

    const paginatedAssignments = filteredAssignments.slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage
    );

    if (loading) {
        return <LoadingState message="Loading Assignments..." variant="research" size="large" fullPage />;
    }

    if (error) {
        return <ErrorState error={error} onRetry={fetchAllData} title="Unable to Load Assignments" variant="research" fullPage retryText="Retry Loading" />;
    }

    return (
        <>
            <FullWidthContainer >
                <PlatformAdminBar notificationCount={65} />

                {/* Header Section */}
                <Paper elevation={2} sx={{ 
                    p: { xs: 2, sm: 3 }, 
                    mb: { xs: 2, sm: 3 }, 
                    borderRadius: 3,
                    backgroundColor: 'white',
                }}>
                    <Box sx={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center', 
                        flexWrap: 'wrap', 
                        gap: 2,
                        flexDirection: { xs: 'column', sm: 'row' }
                    }}>
                        <Typography variant="h5" sx={{ 
                            fontWeight: 'bold', 
                            color: theme.palette.primary.main, 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: 1,
                            fontSize: { xs: '1.25rem', sm: '1.5rem' }
                        }}>
                            <Assignment sx={{ fontSize: { xs: 24, sm: 32 } }} /> 
                            Assignment Management
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                            <Button 
                                variant="outlined" 
                                startIcon={<Download />} 
                                onClick={handleExportData}
                                size={isMobile ? "small" : "medium"}
                                sx={{ textTransform: 'none', borderRadius: 2 }}
                            >
                                Export
                            </Button>
                            <Button 
                                variant="outlined" 
                                startIcon={<Print />} 
                                onClick={handlePrint}
                                size={isMobile ? "small" : "medium"}
                                sx={{ textTransform: 'none', borderRadius: 2 }}
                            >
                                Print
                            </Button>
                            <Button 
                                variant="contained" 
                                startIcon={<RefreshIcon />} 
                                onClick={() => fetchAllData()}
                                size={isMobile ? "small" : "medium"}
                                sx={{ textTransform: 'none', borderRadius: 2 }}
                            >
                                Refresh
                            </Button>
                        </Box>
                    </Box>

                    {/* Stats Cards */}
                    {stats && (
                        <Grid container spacing={2} sx={{ mt: 2 }}>
                            <Grid item xs={6} sm={3}>
                                <Card sx={{ 
                                    textAlign: 'center', 
                                    bgcolor: theme.palette.primary.light + '10',
                                    transition: 'transform 0.2s',
                                    '&:hover': { transform: 'translateY(-4px)' }
                                }}>
                                    <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                                        <Typography variant="h4" sx={{ 
                                            fontWeight: 'bold',
                                            fontSize: { xs: '1.5rem', sm: '2rem' }
                                        }}>
                                            {stats.total_assignments || 0}
                                        </Typography>
                                        <Typography variant="caption" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                                            Total Assignments
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                            <Grid item xs={6} sm={3}>
                                <Card sx={{ 
                                    textAlign: 'center', 
                                    bgcolor: theme.palette.success.light + '10',
                                    transition: 'transform 0.2s',
                                    '&:hover': { transform: 'translateY(-4px)' }
                                }}>
                                    <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                                        <Typography variant="h4" sx={{ 
                                            fontWeight: 'bold',
                                            fontSize: { xs: '1.5rem', sm: '2rem' }
                                        }}>
                                            {stats.active_assignments || 0}
                                        </Typography>
                                        <Typography variant="caption" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                                            Active
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                            <Grid item xs={6} sm={3}>
                                <Card sx={{ 
                                    textAlign: 'center', 
                                    bgcolor: theme.palette.warning.light + '10',
                                    transition: 'transform 0.2s',
                                    '&:hover': { transform: 'translateY(-4px)' }
                                }}>
                                    <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                                        <Typography variant="h4" sx={{ 
                                            fontWeight: 'bold',
                                            fontSize: { xs: '1.5rem', sm: '2rem' }
                                        }}>
                                            {stats.pending_assignments || 0}
                                        </Typography>
                                        <Typography variant="caption" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                                            Pending
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                            <Grid item xs={6} sm={3}>
                                <Card sx={{ 
                                    textAlign: 'center', 
                                    bgcolor: theme.palette.info.light + '10',
                                    transition: 'transform 0.2s',
                                    '&:hover': { transform: 'translateY(-4px)' }
                                }}>
                                    <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                                        <Typography variant="h4" sx={{ 
                                            fontWeight: 'bold',
                                            fontSize: { xs: '1.5rem', sm: '2rem' }
                                        }}>
                                            {stats.completed_assignments || 0}
                                        </Typography>
                                        <Typography variant="caption" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                                            Completed
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                        </Grid>
                    )}
                </Paper>

                {/* Search and Filter Bar */}
                <Paper elevation={2} sx={{ 
                    p: 2, 
                    mb: { xs: 2, sm: 3 }, 
                    borderRadius: 3,
                    backgroundColor: 'white'
                }}>
                    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
                        <TextField
                            fullWidth
                            placeholder="Search by student name, email, registration ID, supervisor name, or assignment ID..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            size="small"
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Search color="action" />
                                    </InputAdornment>
                                ),
                                endAdornment: searchTerm && (
                                    <InputAdornment position="end">
                                        <IconButton size="small" onClick={() => setSearchTerm('')}>
                                            <Clear />
                                        </IconButton>
                                    </InputAdornment>
                                )
                            }}
                            sx={{ flex: 2 }}
                        />
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            <Button
                                variant="outlined"
                                startIcon={<FilterList />}
                                onClick={() => setExpandedFilters(!expandedFilters)}
                                size={isMobile ? "small" : "medium"}
                                sx={{ textTransform: 'none' }}
                            >
                                Filters
                            </Button>
                            {(searchTerm || statusFilter !== 'all' || dateRange.start || dateRange.end) && (
                                <Button
                                    variant="text"
                                    startIcon={<Clear />}
                                    onClick={clearFilters}
                                    size={isMobile ? "small" : "medium"}
                                    sx={{ textTransform: 'none' }}
                                >
                                    Clear
                                </Button>
                            )}
                            <Box sx={{ display: 'flex', gap: 0.5, ml: 'auto' }}>
                                <Tooltip title="Grid View">
                                    <IconButton 
                                        onClick={() => setViewMode('grid')}
                                        color={viewMode === 'grid' ? 'primary' : 'default'}
                                        size={isMobile ? "small" : "medium"}
                                    >
                                        <GridView />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title="List View">
                                    <IconButton 
                                        onClick={() => setViewMode('list')}
                                        color={viewMode === 'list' ? 'primary' : 'default'}
                                        size={isMobile ? "small" : "medium"}
                                    >
                                        <ListIcon />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                        </Box>
                    </Box>

                    <Collapse in={expandedFilters}>
                        <Box sx={{ mt: 2, pt: 2, borderTop: `1px solid ${theme.palette.divider}` }}>
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={6} md={3}>
                                    <FormControl fullWidth size="small">
                                        <InputLabel>Status Filter</InputLabel>
                                        <Select
                                            value={statusFilter}
                                            onChange={(e) => setStatusFilter(e.target.value)}
                                            label="Status Filter"
                                        >
                                            <MenuItem value="all">All Status</MenuItem>
                                            <MenuItem value="active">Active</MenuItem>
                                            <MenuItem value="pending">Pending</MenuItem>
                                            <MenuItem value="completed">Completed</MenuItem>
                                            <MenuItem value="cancelled">Cancelled</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12} sm={6} md={3}>
                                    <TextField
                                        fullWidth
                                        type="date"
                                        label="From Date"
                                        size="small"
                                        value={dateRange.start}
                                        onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
                                        InputLabelProps={{ shrink: true }}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6} md={3}>
                                    <TextField
                                        fullWidth
                                        type="date"
                                        label="To Date"
                                        size="small"
                                        value={dateRange.end}
                                        onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
                                        InputLabelProps={{ shrink: true }}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6} md={3}>
                                    <FormControl fullWidth size="small">
                                        <InputLabel>Sort By</InputLabel>
                                        <Select
                                            value={sortBy}
                                            onChange={(e) => setSortBy(e.target.value)}
                                            label="Sort By"
                                        >
                                            <MenuItem value="assigned_date">Assigned Date</MenuItem>
                                            <MenuItem value="student_name">Student Name</MenuItem>
                                            <MenuItem value="supervisor_name">Supervisor Name</MenuItem>
                                            <MenuItem value="status">Status</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Grid>
                            </Grid>
                        </Box>
                    </Collapse>
                </Paper>

                {/* Tabs */}
                <Paper elevation={2} sx={{ 
                    borderRadius: 3, 
                    overflow: 'hidden',
                    backgroundColor: 'white',
                    mb: { xs: 2, sm: 3 }
                }}>
                    <Tabs
                        value={activeTab}
                        onChange={(e, v) => setActiveTab(v)}
                        sx={{ 
                            borderBottom: 1, 
                            borderColor: 'divider', 
                            px: { xs: 1, sm: 2 }, 
                            pt: 2,
                            '& .MuiTab-root': {
                                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                minWidth: { xs: 'auto', sm: 120 },
                                px: { xs: 1, sm: 2 }
                            }
                        }}
                        variant={isMobile ? "scrollable" : "standard"}
                        scrollButtons={isMobile ? "auto" : false}
                    >
                        <Tab label={`All (${assignments.length})`} />
                        <Tab label={`Active (${stats?.active_assignments || 0})`} />
                        <Tab label={`Pending (${stats?.pending_assignments || 0})`} />
                        <Tab label={`Completed (${stats?.completed_assignments || 0})`} />
                    </Tabs>
                </Paper>

                {/* Results Summary */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, px: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                        Showing {paginatedAssignments.length} of {filteredAssignments.length} assignments
                    </Typography>
                    {filteredAssignments.length > 0 && (
                        <Typography variant="caption" color="text.secondary">
                            Page {page + 1} of {Math.ceil(filteredAssignments.length / rowsPerPage)}
                        </Typography>
                    )}
                </Box>

                {/* Assignments Display */}
                <AnimatePresence mode="wait">
                    {filteredAssignments.length === 0 ? (
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -20 }}
                        >
                            <Paper sx={{ 
                                p: { xs: 4, sm: 8 }, 
                                textAlign: 'center', 
                                borderRadius: 3,
                                backgroundColor: 'white'
                            }}>
                                <Assignment sx={{ fontSize: { xs: 48, sm: 60 }, color: 'text.secondary', mb: 2 }} />
                                <Typography variant="h6" color="text.secondary" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                                    No assignments found
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {searchTerm || statusFilter !== 'all' || dateRange.start || dateRange.end 
                                        ? 'Try adjusting your filters' 
                                        : 'No assignments have been created yet'}
                                </Typography>
                                {(searchTerm || statusFilter !== 'all' || dateRange.start || dateRange.end) && (
                                    <Button onClick={clearFilters} sx={{ mt: 2 }}>
                                        Clear Filters
                                    </Button>
                                )}
                            </Paper>
                        </motion.div>
                    ) : viewMode === 'grid' ? (
                        // Grid View
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -20 }}
                        >
                            <Grid container spacing={2}>
                                {paginatedAssignments.map((assignment, index) => {
                                    const student = assignment.student;
                                    const supervisor = assignment.supervisor;
                                    const status = statusConfig[assignment.status] || statusConfig.pending;
                                    
                                    return (
                                        <Grid item xs={12} sm={6} lg={4} key={assignment.id}>
                                            <Zoom in={true} style={{ transitionDelay: `${index * 50}ms` }}>
                                                <Card sx={{ 
                                                    height: '100%', 
                                                    display: 'flex', 
                                                    flexDirection: 'column',
                                                    transition: 'transform 0.2s, box-shadow 0.2s',
                                                    '&:hover': {
                                                        transform: 'translateY(-4px)',
                                                        boxShadow: theme.shadows[8]
                                                    },
                                                    borderRadius: 2
                                                }}>
                                                    <CardContent sx={{ flexGrow: 1, p: 2 }}>
                                                        {/* Header */}
                                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                                                            <Chip 
                                                                icon={status.icon}
                                                                label={status.label} 
                                                                size="small" 
                                                                color={status.color}
                                                            />
                                                            <Typography variant="caption" color="text.secondary">
                                                                ID: {assignment.assignment_id}
                                                            </Typography>
                                                        </Box>

                                                        {/* Student Info */}
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                                                            <Avatar 
                                                                src={getProfileImageUrl(student)}
                                                                sx={{ 
                                                                    width: 40, 
                                                                    height: 40,
                                                                    bgcolor: !getProfileImageUrl(student) ? stringToColor(student?.name) : 'transparent'
                                                                }}
                                                            >
                                                                {!getProfileImageUrl(student) && getInitials(student?.name)}
                                                            </Avatar>
                                                            <Box sx={{ flex: 1 }}>
                                                                <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                                                                    {student?.name || 'N/A'}
                                                                </Typography>
                                                                <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                                    <School sx={{ fontSize: 12 }} />
                                                                    {student?.registration_id || 'N/A'}
                                                                </Typography>
                                                            </Box>
                                                        </Box>

                                                        {/* Supervisor Info */}
                                                        <Box sx={{ 
                                                            display: 'flex', 
                                                            alignItems: 'center', 
                                                            gap: 1.5, 
                                                            mb: 2,
                                                            p: 1,
                                                            bgcolor: theme.palette.grey[50],
                                                            borderRadius: 1
                                                        }}>
                                                            <Avatar 
                                                                src={getProfileImageUrl(supervisor, 'supervisor')}
                                                                sx={{ width: 32, height: 32 }}
                                                            >
                                                                {getInitials(supervisor?.name)}
                                                            </Avatar>
                                                            <Box>
                                                                <Typography variant="caption" color="text.secondary">
                                                                    Supervisor
                                                                </Typography>
                                                                <Typography variant="body2">
                                                                    {supervisor?.name || 'N/A'}
                                                                </Typography>
                                                            </Box>
                                                        </Box>

                                                        {/* Assignment Details */}
                                                        <Stack spacing={0.5}>
                                                            <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                                <CalendarToday sx={{ fontSize: 12 }} />
                                                                Assigned: {formatCreatedAtDate(assignment.assigned_date)}
                                                            </Typography>
                                                            {assignment.completion_date && (
                                                                <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                                    <CheckCircle sx={{ fontSize: 12 }} />
                                                                    Completed: {formatCreatedAtDate(assignment.completion_date)}
                                                                </Typography>
                                                            )}
                                                        </Stack>

                                                        {/* Notes Preview */}
                                                        {assignment.assignment_notes && (
                                                            <Typography variant="caption" color="text.secondary" sx={{ 
                                                                mt: 1.5, 
                                                                p: 1, 
                                                                bgcolor: theme.palette.grey[50], 
                                                                borderRadius: 1,
                                                                display: 'block',
                                                                whiteSpace: 'nowrap',
                                                                overflow: 'hidden',
                                                                textOverflow: 'ellipsis'
                                                            }}>
                                                                📝 {assignment.assignment_notes.substring(0, 60)}...
                                                            </Typography>
                                                        )}
                                                    </CardContent>

                                                    <CardActions sx={{ p: 2, pt: 0 }}>
                                                        <Button 
                                                            fullWidth
                                                            variant="outlined"
                                                            startIcon={<Visibility />}
                                                            onClick={() => handleOpenViewDialog(assignment)}
                                                            size="small"
                                                            sx={{ textTransform: 'none', borderRadius: 2 }}
                                                        >
                                                            View Details
                                                        </Button>
                                                    </CardActions>
                                                </Card>
                                            </Zoom>
                                        </Grid>
                                    );
                                })}
                            </Grid>
                        </motion.div>
                    ) : (
                        // List View
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -20 }}
                        >
                            <Paper sx={{ borderRadius: 3, overflow: 'hidden' }}>
                                <TableContainer sx={{ maxHeight: 'calc(100vh - 400px)' }}>
                                    <Table stickyHeader>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>
                                                    <TableSortLabel
                                                        active={sortBy === 'student_name'}
                                                        direction={sortBy === 'student_name' ? sortOrder : 'asc'}
                                                        onClick={() => handleSort('student_name')}
                                                    >
                                                        Student
                                                    </TableSortLabel>
                                                </TableCell>
                                                <TableCell>
                                                    <TableSortLabel
                                                        active={sortBy === 'supervisor_name'}
                                                        direction={sortBy === 'supervisor_name' ? sortOrder : 'asc'}
                                                        onClick={() => handleSort('supervisor_name')}
                                                    >
                                                        Supervisor
                                                    </TableSortLabel>
                                                </TableCell>
                                                <TableCell>Assignment ID</TableCell>
                                                <TableCell>
                                                    <TableSortLabel
                                                        active={sortBy === 'status'}
                                                        direction={sortBy === 'status' ? sortOrder : 'asc'}
                                                        onClick={() => handleSort('status')}
                                                    >
                                                        Status
                                                    </TableSortLabel>
                                                </TableCell>
                                                <TableCell>
                                                    <TableSortLabel
                                                        active={sortBy === 'assigned_date'}
                                                        direction={sortBy === 'assigned_date' ? sortOrder : 'asc'}
                                                        onClick={() => handleSort('assigned_date')}
                                                    >
                                                        Assigned Date
                                                    </TableSortLabel>
                                                </TableCell>
                                                <TableCell align="center">Actions</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {paginatedAssignments.map((assignment) => {
                                                const status = statusConfig[assignment.status] || statusConfig.pending;
                                                
                                                return (
                                                    <TableRow key={assignment.id} hover>
                                                        <TableCell>
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                                                <Avatar 
                                                                    src={getProfileImageUrl(assignment.student)}
                                                                    sx={{ width: 32, height: 32 }}
                                                                >
                                                                    {getInitials(assignment.student?.name)}
                                                                </Avatar>
                                                                <Box>
                                                                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                                        {assignment.student?.name || 'N/A'}
                                                                    </Typography>
                                                                    <Typography variant="caption" color="text.secondary">
                                                                        {assignment.student?.email || 'N/A'}
                                                                    </Typography>
                                                                </Box>
                                                            </Box>
                                                        </TableCell>
                                                        <TableCell>
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                                                <Avatar 
                                                                    src={getProfileImageUrl(assignment.supervisor, 'supervisor')}
                                                                    sx={{ width: 32, height: 32 }}
                                                                >
                                                                    {getInitials(assignment.supervisor?.name)}
                                                                </Avatar>
                                                                <Box>
                                                                    <Typography variant="body2">
                                                                        {assignment.supervisor?.name || 'N/A'}
                                                                    </Typography>
                                                                    <Typography variant="caption" color="text.secondary">
                                                                        {assignment.supervisor?.email || 'N/A'}
                                                                    </Typography>
                                                                </Box>
                                                            </Box>
                                                        </TableCell>
                                                        <TableCell>
                                                            <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                                                                {assignment.assignment_id}
                                                            </Typography>
                                                        </TableCell>
                                                        <TableCell>
                                                            <Chip 
                                                                icon={status.icon}
                                                                label={status.label} 
                                                                size="small" 
                                                                color={status.color}
                                                            />
                                                        </TableCell>
                                                        <TableCell>
                                                            <Typography variant="body2">
                                                                {formatCreatedAtDate(assignment.assigned_date)}
                                                            </Typography>
                                                        </TableCell>
                                                        <TableCell align="center">
                                                            <Tooltip title="View Details">
                                                                <IconButton 
                                                                    size="small" 
                                                                    onClick={() => handleOpenViewDialog(assignment)}
                                                                    color="primary"
                                                                >
                                                                    <Visibility />
                                                                </IconButton>
                                                            </Tooltip>
                                                        </TableCell>
                                                    </TableRow>
                                                );
                                            })}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </Paper>
                        </motion.div>
                    )}
                </AnimatePresence>

                {/* Pagination */}
                {filteredAssignments.length > 0 && (
                    <Paper sx={{ mt: 3, p: 2, borderRadius: 3, display: 'flex', justifyContent: 'center' }}>
                        <TablePagination
                            component="div"
                            count={filteredAssignments.length}
                            page={page}
                            onPageChange={(e, newPage) => setPage(newPage)}
                            rowsPerPage={rowsPerPage}
                            onRowsPerPageChange={(e) => {
                                setRowsPerPage(parseInt(e.target.value, 10));
                                setPage(0);
                            }}
                            rowsPerPageOptions={[6, 12, 24, 48]}
                            labelRowsPerPage={isMobile ? "Rows:" : "Rows per page:"}
                        />
                    </Paper>
                )}

                {/* View Details Dialog */}
                <Dialog
                    open={openViewDialog}
                    onClose={handleCloseViewDialog}
                    maxWidth="md"
                    fullWidth
                    PaperProps={{
                        sx: {
                            m: { xs: 1, sm: 2 },
                            width: { xs: 'calc(100% - 16px)', sm: 'auto' },
                            maxHeight: '90vh',
                            borderRadius: 3
                        }
                    }}
                >
                    {selectedAssignment && (
                        <>
                            <DialogTitle sx={{ 
                                fontSize: { xs: '1rem', sm: '1.25rem' }, 
                                borderBottom: `1px solid ${theme.palette.divider}`,
                                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                                color: 'white'
                            }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Assignment />
                                        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                                            Assignment Details
                                        </Typography>
                                    </Box>
                                    <IconButton onClick={handleCloseViewDialog} sx={{ color: 'white' }}>
                                        <Close />
                                    </IconButton>
                                </Box>
                            </DialogTitle>
                            <DialogContent>
                                <Box sx={{ pt: 2 }}>
                                    {/* Status Badge */}
                                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                                        <Chip 
                                            icon={statusConfig[selectedAssignment.status]?.icon}
                                            label={statusConfig[selectedAssignment.status]?.label}
                                            color={statusConfig[selectedAssignment.status]?.color}
                                            size="medium"
                                        />
                                    </Box>

                                    <Grid container spacing={3}>
                                        {/* Student Information */}
                                        <Grid item xs={12} md={6}>
                                            <Paper sx={{ p: 2, bgcolor: theme.palette.grey[50], borderRadius: 2 }}>
                                                <Typography variant="subtitle1" sx={{ 
                                                    fontWeight: 'bold', 
                                                    color: theme.palette.primary.main, 
                                                    mb: 2,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 1
                                                }}>
                                                    <Person fontSize="small" />
                                                    Student Information
                                                </Typography>
                                                <Stack spacing={1.5}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                        <Avatar 
                                                            src={getProfileImageUrl(selectedAssignment.student)}
                                                            sx={{ width: 60, height: 60 }}
                                                        >
                                                            {getInitials(selectedAssignment.student?.name)}
                                                        </Avatar>
                                                        <Box>
                                                            <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                                                                {selectedAssignment.student?.name}
                                                            </Typography>
                                                            <Typography variant="caption" color="text.secondary">
                                                                {selectedAssignment.student?.registration_id}
                                                            </Typography>
                                                        </Box>
                                                    </Box>
                                                    <Divider />
                                                    <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        <Email fontSize="small" color="action" />
                                                        {selectedAssignment.student?.email}
                                                    </Typography>
                                                    {selectedAssignment.student?.contact_phone && (
                                                        <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                            <Phone fontSize="small" color="action" />
                                                            {selectedAssignment.student?.contact_phone}
                                                        </Typography>
                                                    )}
                                                    {selectedAssignment.student?.nationality && (
                                                        <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                            <Flag fontSize="small" color="action" />
                                                            {selectedAssignment.student?.nationality}
                                                        </Typography>
                                                    )}
                                                    <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        <School fontSize="small" color="action" />
                                                        {selectedAssignment.student?.university || 'University not specified'}
                                                    </Typography>
                                                </Stack>
                                            </Paper>
                                        </Grid>

                                        {/* Supervisor Information */}
                                        <Grid item xs={12} md={6}>
                                            <Paper sx={{ p: 2, bgcolor: theme.palette.grey[50], borderRadius: 2 }}>
                                                <Typography variant="subtitle1" sx={{ 
                                                    fontWeight: 'bold', 
                                                    color: theme.palette.primary.main, 
                                                    mb: 2,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 1
                                                }}>
                                                    <SupervisorAccount fontSize="small" />
                                                    Supervisor Information
                                                </Typography>
                                                <Stack spacing={1.5}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                        <Avatar 
                                                            src={getProfileImageUrl(selectedAssignment.supervisor, 'supervisor')}
                                                            sx={{ width: 60, height: 60 }}
                                                        >
                                                            {getInitials(selectedAssignment.supervisor?.name)}
                                                        </Avatar>
                                                        <Box>
                                                            <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                                                                {selectedAssignment.supervisor?.name}
                                                            </Typography>
                                                            <Typography variant="caption" color="text.secondary">
                                                                Supervisor
                                                            </Typography>
                                                        </Box>
                                                    </Box>
                                                    <Divider />
                                                    <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        <Email fontSize="small" color="action" />
                                                        {selectedAssignment.supervisor?.email}
                                                    </Typography>
                                                    {selectedAssignment.supervisor?.contact_phone && (
                                                        <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                            <Phone fontSize="small" color="action" />
                                                            {selectedAssignment.supervisor?.contact_phone}
                                                        </Typography>
                                                    )}
                                                </Stack>
                                            </Paper>
                                        </Grid>

                                        {/* Assignment Information */}
                                        <Grid item xs={12}>
                                            <Paper sx={{ p: 2, bgcolor: theme.palette.grey[50], borderRadius: 2 }}>
                                                <Typography variant="subtitle1" sx={{ 
                                                    fontWeight: 'bold', 
                                                    color: theme.palette.primary.main, 
                                                    mb: 2,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 1
                                                }}>
                                                    <Assignment fontSize="small" />
                                                    Assignment Information
                                                </Typography>
                                                <Grid container spacing={2}>
                                                    <Grid item xs={12} sm={6}>
                                                        <Typography variant="body2">
                                                            <strong>Assignment ID:</strong> {selectedAssignment.assignment_id}
                                                        </Typography>
                                                    </Grid>
                                                    <Grid item xs={12} sm={6}>
                                                        <Typography variant="body2">
                                                            <strong>Assigned Date:</strong> {formatCreatedAtDate(selectedAssignment.assigned_date)}
                                                        </Typography>
                                                    </Grid>
                                                    {selectedAssignment.completion_date && (
                                                        <Grid item xs={12} sm={6}>
                                                            <Typography variant="body2">
                                                                <strong>Completion Date:</strong> {formatCreatedAtDate(selectedAssignment.completion_date)}
                                                            </Typography>
                                                        </Grid>
                                                    )}
                                                </Grid>
                                                {selectedAssignment.assignment_notes && (
                                                    <>
                                                        <Divider sx={{ my: 2 }} />
                                                        <Typography variant="body2">
                                                            <strong>Notes:</strong>
                                                        </Typography>
                                                        <Typography variant="body2" sx={{ mt: 1, whiteSpace: 'pre-wrap' }}>
                                                            {selectedAssignment.assignment_notes}
                                                        </Typography>
                                                    </>
                                                )}
                                            </Paper>
                                        </Grid>
                                    </Grid>
                                </Box>
                            </DialogContent>
                            <DialogActions sx={{ p: 2 }}>
                                <Button onClick={handleCloseViewDialog} variant="contained">
                                    Close
                                </Button>
                            </DialogActions>
                        </>
                    )}
                </Dialog>
            </FullWidthContainer>
        </>
    );
};

export default Assigned;